import { SalesPerformanceRow, StoreSalesPerformanceResponse } from '../../../../../hooks';
import { getAggregatedDataFilterByIndex, getIndex } from '../sales.util';

// --- EQUATION WIDGET HELPERS --- //

export type EquationTimePeriod = keyof Pick<
  StoreSalesPerformanceResponse,
  'currentDay' | 'currentWeek' | 'lastCompleteWeek' | 'lastFourWeeks'
>;

type AggregatedData = {
  currentDay: SalesPerformanceRow;
  currentWeek: SalesPerformanceRow;
  lastCompleteWeek: SalesPerformanceRow;
  lastFourWeeks: SalesPerformanceRow;
  accumulatedFinancialYearToDate: SalesPerformanceRow;
};

export type EquationItem = {
  numbers: number;
  vsLy: number;
  vsArea: number;
  vsSM: number;
};

export type EquationData = {
  [key in keyof Partial<StoreSalesPerformanceResponse>]: {
    traffic: EquationItem;
    conversion: EquationItem;
    ppr: EquationItem;
    pricePerPiece: EquationItem;
    avgPurchase: EquationItem;
    revVisitor: EquationItem;
  };
};

const calculateRatio = (value1: number, value2: number) => {
  if (value2 === 0) return 0;
  const value = value1 / value2;
  return value;
};

const computeEquationItem = (
  numbers: number,
  ly: number,
  numbersArea: number,
  numberLYArea: number,
  numbersSM: number,
  numberLYSM: number,
) => {
  const idxLY = getIndex(numbers, ly);
  const idxArea = getIndex(numbersArea, numberLYArea);
  const idxSM = getIndex(numbersSM, numberLYSM);

  return {
    numbers,
    vsLy: idxLY,
    vsArea: getIndex(idxLY, idxArea),
    vsSM: getIndex(idxLY, idxSM),
  };
};

const computeMetricToEquationItemMap = (
  response: StoreSalesPerformanceResponse,
  aggregatedData: AggregatedData,
  period: EquationTimePeriod,
) => {
  const { visitors, receipts } = response[period];
  const aggregatedByPeriod = aggregatedData[period];

  // calculate conversion rate
  const conversionThisYear = calculateRatio(receipts.thisYear, visitors.thisYear);
  const conversionLastYear = calculateRatio(receipts.lastYear, visitors.lastYear);
  const conversionThisYearArea = calculateRatio(receipts.thisYearArea, visitors.thisYearArea);
  const conversionLastYearArea = calculateRatio(receipts.lastYearArea, visitors.lastYearArea);
  const conversionThisYearMarket = calculateRatio(receipts.thisYearMarket, visitors.thisYearMarket);
  const conversionLastYearMarket = calculateRatio(receipts.lastYearMarket, visitors.lastYearMarket);

  // Calculate average Purchase
  const avgPurchaseThisYear = calculateRatio(aggregatedByPeriod.salesNet, receipts.thisYear);
  const avgPurchaseLastYear = calculateRatio(aggregatedByPeriod.salesNetLastYear, receipts.lastYear);
  const avgPurchaseThisYearArea = calculateRatio(aggregatedByPeriod.salesNetArea, receipts.thisYearArea);
  const avgPurchaseLastYearArea = calculateRatio(aggregatedByPeriod.salesNetAreaLastYear, receipts.lastYearArea);
  const avgPurchaseThisYearMarket = calculateRatio(aggregatedByPeriod.salesNetMarket, receipts.thisYearMarket);
  const avgPurchaseLastYearMarket = calculateRatio(
    aggregatedByPeriod.salesNetMarketLastYear,
    receipts.lastYearMarket,
  );

  const trafficEquationItem = computeEquationItem(
    visitors.thisYear,
    visitors.lastYear,
    visitors.thisYearArea,
    visitors.lastYearArea,
    visitors.thisYearMarket,
    visitors.lastYearMarket,
  );

  const conversionEquationItem = computeEquationItem(
    conversionThisYear * 100,
    conversionLastYear * 100,
    conversionThisYearArea * 100,
    conversionLastYearArea * 100,
    conversionThisYearMarket * 100,
    conversionLastYearMarket * 100,
  );

  const pprEquationItem = computeEquationItem(
    calculateRatio(aggregatedByPeriod.soldPieces, receipts.thisYear),
    calculateRatio(aggregatedByPeriod.soldPiecesLastYear, receipts.lastYear),
    calculateRatio(aggregatedByPeriod.soldPiecesArea, receipts.thisYearArea),
    calculateRatio(aggregatedByPeriod.soldPiecesAreaLastYear, receipts.lastYearArea),
    calculateRatio(aggregatedByPeriod.soldPiecesMarket, receipts.thisYearMarket),
    calculateRatio(aggregatedByPeriod.soldPiecesMarketLastYear, receipts.lastYearMarket),
  );

  const pricePerPieceEquationItem = computeEquationItem(
    calculateRatio(aggregatedByPeriod.salesNet, aggregatedByPeriod.soldPieces),
    calculateRatio(aggregatedByPeriod.salesNetLastYear, aggregatedByPeriod.soldPiecesLastYear),
    calculateRatio(aggregatedByPeriod.salesNetArea, aggregatedByPeriod.soldPiecesArea),
    calculateRatio(aggregatedByPeriod.salesNetAreaLastYear, aggregatedByPeriod.soldPiecesAreaLastYear),
    calculateRatio(aggregatedByPeriod.salesNetMarket, aggregatedByPeriod.soldPiecesMarket),
    calculateRatio(aggregatedByPeriod.salesNetMarketLastYear, aggregatedByPeriod.soldPiecesMarketLastYear),
  );

  const avgPurchaseEquationItem = computeEquationItem(
    avgPurchaseThisYear,
    avgPurchaseLastYear,
    avgPurchaseThisYearArea,
    avgPurchaseLastYearArea,
    avgPurchaseThisYearMarket,
    avgPurchaseLastYearMarket,
  );

  const revVisitorEquationItem = computeEquationItem(
    avgPurchaseThisYear * conversionThisYear,
    avgPurchaseLastYear * conversionLastYear,
    avgPurchaseThisYearArea * conversionThisYearArea,
    avgPurchaseLastYearArea * conversionLastYearArea,
    avgPurchaseThisYearMarket * conversionThisYearMarket,
    avgPurchaseLastYearMarket * conversionLastYearMarket,
  );

  return {
    traffic: trafficEquationItem,
    conversion: conversionEquationItem,
    ppr: pprEquationItem,
    pricePerPiece: pricePerPieceEquationItem,
    avgPurchase: avgPurchaseEquationItem,
    revVisitor: revVisitorEquationItem,
  };
};

export const getEquationData = (response: StoreSalesPerformanceResponse, filterIndex?: string): EquationData => {
  const aggregatedData = getAggregatedDataFilterByIndex(response, filterIndex);

  const computedData = {
    currentDay: computeMetricToEquationItemMap(response, aggregatedData, 'currentDay'),
    currentWeek: computeMetricToEquationItemMap(response, aggregatedData, 'currentWeek'),
    lastCompleteWeek: computeMetricToEquationItemMap(response, aggregatedData, 'lastCompleteWeek'),
    lastFourWeeks: computeMetricToEquationItemMap(response, aggregatedData, 'lastFourWeeks'),
  };

  return computedData;
};

// Get formatter tools based on metric name
export const getMetricFormatSpec = (metricName: string) => {
  let formatSpec: { decimals: number; unit: string | null } = {
    decimals: 0,
    unit: null,
  };

  if (metricName === 'Conversion') {
    formatSpec = {
      decimals: 1,
      unit: '%',
    };
  } else if (metricName === 'PPR' || metricName === 'ppr') {
    formatSpec = {
      decimals: 1,
      unit: null,
    };
  } else {
    formatSpec = {
      decimals: 0,
      unit: null,
    };
  }
  return formatSpec;
};
